import { offeredSwatches } from './catalogue-diff'
import type { LayerRole } from './layer-naming'
import { ROLE_BLEND, stackLayers, unlocksAtTier } from './layer-stack'
import type { CssBlend } from './layer-stack'
import type { BoCatalog, BoOption, BoSection, RenderFit } from './types'

/**
 * The studio's paint order for a section with no explicit `paintOrder`. Mirrors
 * `DEFAULT_PAINT_ORDER` in `@b9/avatar-ui`; a section not listed there paints last.
 */
export const FALLBACK_PAINT_ORDER: readonly string[] = [
  'body',
  'shirt',
  'scarf',
  'ears',
  'face',
  'mouth',
  'eyes',
  'nose',
  'eyebrows',
  'glasses',
  'hair',
]

const BACK_ROLES: readonly LayerRole[] = [
  'hairBack',
  'hairBackShadow',
  'hairBackHighlight',
  'hairBackOutline',
]

const isBack = (role: LayerRole): boolean => BACK_ROLES.includes(role)

export interface PreviewLayer {
  key: string
  sectionId: string
  kind: 'image' | 'fill'
  url?: string
  colour?: string
  blend: CssBlend
  opacity: number
  fit: RenderFit
  order: number
}

export interface ComposedAvatar {
  layers: PreviewLayer[]
  /** Sections that paint but had nothing to draw, so the gap is visible rather than silent. */
  missing: string[]
}

export interface ComposeInput {
  catalogue: BoCatalog
  selectedSectionId?: string
  /** The option being tried in the selected section. Falls back to that section's own default. */
  selectedSlug?: string
  selectedSwatch?: string
}

function effectiveOrder(section: BoSection): number | null {
  if (section.paintOrder !== null) {
    return section.paintOrder
  }
  const fallback = FALLBACK_PAINT_ORDER.indexOf(section.id)

  return fallback < 0 ? null : 1000 + fallback
}

/**
 * The option a section shows when it is not the one being edited.
 *
 * `set.defaults` first — but it is EMPTY on both real sets today, so the first option in sort order
 * is the working fallback. Without it a preview of one section would render that section alone on a
 * blank canvas, which answers nothing about whether the artwork sits right.
 */
export function defaultOptionFor(
  catalogue: BoCatalog,
  sectionId: string,
): BoOption | undefined {
  const declared = catalogue.set.defaults?.[sectionId]
  const inSection = catalogue.options
    .filter((o) => o.category === sectionId && o.status === 'active')
    .sort((a, b) =>
      a.sortOrder === b.sortOrder
        ? a.slug.localeCompare(b.slug)
        : a.sortOrder - b.sortOrder,
    )

  const byDeclaredName = declared
    ? inSection.find((o) => o.slug === declared)
    : undefined

  return byDeclaredName ?? inSection[0]
}

export function composeAvatar({
  catalogue,
  selectedSectionId,
  selectedSlug,
  selectedSwatch,
}: ComposeInput): ComposedAvatar {
  const layers: PreviewLayer[] = []
  const missing: string[] = []

  catalogue.sections.forEach((section) => {
    const order = effectiveOrder(section)
    if (order === null) {
      // No slot: a tint source such as `skin_tone`, which colours other artwork rather than
      // painting a layer of its own.
      return
    }

    if (section.kind === 'color') {
      const colour =
        (section.id === selectedSectionId ? selectedSwatch : undefined) ??
        offeredSwatches(section.swatches, section.retiredSwatches)[0]
      if (!colour) {
        missing.push(section.id)

        return
      }
      layers.push({
        key: `${section.id}-fill`,
        sectionId: section.id,
        kind: 'fill',
        colour: `#${colour}`,
        blend: 'normal',
        opacity: section.renderOpacity,
        fit: section.renderFit,
        order,
      })

      return
    }

    const chosen =
      section.id === selectedSectionId && selectedSlug
        ? catalogue.options.find(
            (o) => o.category === section.id && o.slug === selectedSlug,
          )
        : defaultOptionFor(catalogue, section.id)

    if (!chosen) {
      missing.push(section.id)

      return
    }

    const stack = stackLayers(chosen.layers)
    if (!stack.length) {
      missing.push(section.id)

      return
    }

    stack.forEach((layer) => {
      const back = isBack(layer.role)
      if (back && section.backPaintOrder === null) {
        return
      }
      layers.push({
        key: `${section.id}-${chosen.slug}-${layer.role}`,
        sectionId: section.id,
        kind: 'image',
        url: layer.url,
        blend: ROLE_BLEND[layer.role],
        opacity: section.renderOpacity,
        fit: section.renderFit,
        // A back group is a SEPARATE pass at its own slot, which is how hair sits behind the body.
        order: back ? (section.backPaintOrder as number) : order,
      })
    })
  })

  return {
    layers: layers.sort((a, b) => a.order - b.order),
    missing,
  }
}

export interface PreviewOption {
  option: BoOption
  unlocksAt: number | null
  lockedAtTier: boolean
}

/** The options of the section being previewed, with what the chosen tier can actually use. */
export function previewOptionsFor(
  catalogue: BoCatalog,
  sectionId: string,
  tier: number,
): PreviewOption[] {
  const section = catalogue.sections.find((s) => s.id === sectionId)
  const inSection = catalogue.options
    .filter((o) => o.category === sectionId)
    .sort((a, b) =>
      a.sortOrder === b.sortOrder
        ? a.slug.localeCompare(b.slug)
        : a.sortOrder - b.sortOrder,
    )

  return inSection.map((option, index) => {
    if (option.status !== 'active') {
      return { option, unlocksAt: null, lockedAtTier: false }
    }
    const activeIndex = inSection.slice(0, index).filter((o) => o.status === 'active').length
    const unlocksAt = unlocksAtTier(section?.tierRule ?? null, activeIndex, option.slug)

    return {
      option,
      unlocksAt,
      lockedAtTier: unlocksAt === null ? Boolean(section?.tierRule) : unlocksAt > tier,
    }
  })
}
